import React from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import SystemParametersAuditModal from '../components/SystemParametersAuditModal';
import { IconHistory } from '@tabler/icons-react';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'react-hot-toast';

interface SystemParameters {
  min_price: number;
  max_price: number;
  initial_credits: number;
}

// Cache para los parámetros del sistema
let parametersCache: SystemParameters | null = null;
let lastFetchTime: number = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

export function clearSystemParametersCache() {
  parametersCache = null;
  lastFetchTime = 0;
}

export const SystemParameters: React.FC = () => {
  const navigate = useNavigate();
  const [parameters, setParameters] = React.useState<SystemParameters>({
    min_price: 40.0,
    max_price: 100.0,
    initial_credits: 0,
  });
  const [initialCreditsInput, setInitialCreditsInput] = React.useState<string>('0');
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [isAuditModalOpen, setIsAuditModalOpen] = React.useState(false);
  const [auditRefreshTrigger, setAuditRefreshTrigger] = React.useState(0);

  React.useEffect(() => {
    fetchParameters();
  }, []);

  const fetchParameters = async (forceRefresh = false) => {
    try {
      // Verificar si podemos usar el caché
      const now = Date.now();
      if (!forceRefresh && parametersCache && (now - lastFetchTime) < CACHE_DURATION) {
        setParameters(parametersCache);
        setInitialCreditsInput(String(parametersCache.initial_credits ?? 0));
        setLoading(false);
        return;
      }

      setLoading(true);
      const { data, error } = await supabase()
        .from('system_parameters')
        .select('*')
        .eq('is_active', true)
        .single();

      if (error) throw error;
      if (data) {
        const newParameters: SystemParameters = {
          min_price: parseFloat((data as any).min_price) || 40.0,
          max_price: parseFloat((data as any).max_price) || 100.0,
          initial_credits:
            typeof (data as any).initial_credits === 'number'
              ? (data as any).initial_credits
              : parseInt(String((data as any).initial_credits ?? '0'), 10) || 0,
        };
        setParameters(newParameters);
        setInitialCreditsInput(String(newParameters.initial_credits ?? 0));
        // Actualizar caché
        parametersCache = newParameters;
        lastFetchTime = now;
      }
    } catch (error) {
      console.error('Error al obtener parámetros:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      // 1) Actualizar precios
      const { error: priceError } = await supabase().rpc('update_system_parameters', {
        p_min_price: parameters.min_price,
        p_max_price: parameters.max_price,
      });

      if (priceError) {
        console.error('Error al guardar parámetros de precio:', priceError);
        let errorMessage = 'Error al guardar los parámetros del sistema';

        if (priceError.message.includes('precio máximo debe ser mayor')) {
          errorMessage = 'El precio máximo debe ser mayor que el precio mínimo';
        } else if (priceError.message) {
          errorMessage = `Error: ${priceError.message}`;
        }

        toast.error(errorMessage, { duration: 5000 });
        return;
      }

      // 2) Actualizar créditos iniciales
      const { error: creditsError } = await supabase().rpc('update_initial_credits', {
        p_initial_credits: parameters.initial_credits,
      });

      if (creditsError) {
        console.error('Error al guardar créditos iniciales:', creditsError);
        const errorMessage =
          creditsError.message && creditsError.message.includes('no pueden ser negativos')
            ? 'Los créditos iniciales no pueden ser negativos.'
            : `Error al guardar los créditos iniciales: ${creditsError.message || 'desconocido'}`;

        toast.error(errorMessage, { duration: 5000 });
        return;
      }

      // Forzar actualización después de guardar
      await fetchParameters(true);

      // Refrescar el historial de auditoría si el modal está abierto
      if (isAuditModalOpen) {
        setTimeout(() => {
          setAuditRefreshTrigger((prev) => prev + 1);
        }, 1000);
      }

      toast.success('Parámetros del sistema guardados correctamente', {
        duration: 3000,
      });
    } catch (error) {
      console.error('Error al guardar parámetros:', error);
      const errorMessage =
        error instanceof Error
          ? `Error al guardar: ${error.message}`
          : 'Error desconocido al guardar los parámetros del sistema';

      toast.error(errorMessage, {
        duration: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  const isValid =
    parameters.min_price >= 0 &&
    parameters.max_price > parameters.min_price &&
    parameters.initial_credits >= 0;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-[60vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="flex items-center text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Volver
          </button>
          <h1 className="text-2xl font-bold">Parámetros del Sistema</h1>
        </div>
        <button
          className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 transition"
          onClick={() => setIsAuditModalOpen(true)}
        >
          <IconHistory />
          Ver Historial
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-5 shadow">
            <h2 className="text-lg font-semibold mb-4">Precios del Sistema</h2>
          <div className="space-y-5">
            <label className="block">
              <span className="block text-sm text-gray-300 mb-1">Precio mínimo</span>
              <div className="relative">
                <span className="absolute inset-y-0 left-3 flex items-center text-gray-400">$</span>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  className="w-full rounded-md bg-gray-900 border border-gray-700 pl-7 pr-3 py-2 text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={parameters.min_price || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Permitir vacío o números válidos
                    if (value === '') {
                      setParameters({
                        ...parameters,
                        min_price: 0
                      });
                    } else {
                      const numValue = parseFloat(value);
                      if (!isNaN(numValue) && numValue >= 0) {
                        setParameters({
                          ...parameters,
                          min_price: numValue
                        });
                      }
                    }
                  }}
                  onBlur={(e) => {
                    const value = e.target.value.trim();
                    if (value === '' || isNaN(parseFloat(value)) || parseFloat(value) < 0) {
                      setParameters({
                        ...parameters,
                        min_price: 0
                      });
                    }
                  }}
                />
              </div>
              <span className="text-xs text-gray-400">Precio mínimo aplicado según la fórmula de cálculo.</span>
            </label>

            <label className="block">
              <span className="block text-sm text-gray-300 mb-1">Precio máximo</span>
              <div className="relative">
                <span className="absolute inset-y-0 left-3 flex items-center text-gray-400">$</span>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  className="w-full rounded-md bg-gray-900 border border-gray-700 pl-7 pr-3 py-2 text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={parameters.max_price || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Permitir vacío o números válidos
                    if (value === '') {
                      setParameters({
                        ...parameters,
                        max_price: 0
                      });
                    } else {
                      const numValue = parseFloat(value);
                      if (!isNaN(numValue) && numValue >= 0) {
                        setParameters({
                          ...parameters,
                          max_price: numValue
                        });
                      }
                    }
                  }}
                  onBlur={(e) => {
                    const value = e.target.value.trim();
                    if (value === '' || isNaN(parseFloat(value)) || parseFloat(value) < 0) {
                      setParameters({
                        ...parameters,
                        max_price: 0
                      });
                    }
                  }}
                />
              </div>
              <span className="text-xs text-gray-400">Precio máximo aplicado según la fórmula de cálculo. Debe ser mayor que el precio mínimo.</span>
            </label>
          </div>
          </div>

          <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-5 shadow">
            <h2 className="text-lg font-semibold mb-4">Créditos Iniciales</h2>
            <div className="space-y-3">
              <p className="text-sm text-gray-300">
                Define la cantidad de créditos que recibe automáticamente un nuevo usuario al crear su cuenta.
              </p>
              <label className="block">
                <span className="block text-sm text-gray-300 mb-1">Créditos iniciales por usuario</span>
                <input
                  type="number"
                  min={0}
                  step={1}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className="w-full rounded-md bg-gray-900 border border-gray-700 px-3 py-2 text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={initialCreditsInput}
                  onChange={(e) => {
                    let value = e.target.value;
                    // Permitir borrar todo temporalmente
                    if (value === '') {
                      setInitialCreditsInput('');
                      setParameters({
                        ...parameters,
                        initial_credits: 0,
                      });
                      return;
                    }
                    // Solo dígitos
                    value = value.replace(/\D/g, '');
                    // Eliminar ceros a la izquierda, pero mantener un solo 0 si es el único dígito
                    value = value.replace(/^0+(?=\d)/, '');
                    if (value === '') {
                      setInitialCreditsInput('0');
                      setParameters({
                        ...parameters,
                        initial_credits: 0,
                      });
                      return;
                    }
                    const numValue = parseInt(value, 10);
                    if (!isNaN(numValue) && numValue >= 0) {
                      setInitialCreditsInput(value);
                      setParameters({
                        ...parameters,
                        initial_credits: numValue,
                      });
                    }
                  }}
                  onBlur={(e) => {
                    let value = e.target.value.trim();
                    if (value === '') {
                      setInitialCreditsInput('0');
                      setParameters({
                        ...parameters,
                        initial_credits: 0,
                      });
                      return;
                    }
                    value = value.replace(/\D/g, '').replace(/^0+(?=\d)/, '');
                    if (value === '') {
                      setInitialCreditsInput('0');
                      setParameters({
                        ...parameters,
                        initial_credits: 0,
                      });
                      return;
                    }
                    const numValue = parseInt(value, 10);
                    if (isNaN(numValue) || numValue < 0) {
                      setInitialCreditsInput('0');
                      setParameters({
                        ...parameters,
                        initial_credits: 0,
                      });
                    } else {
                      setInitialCreditsInput(String(numValue));
                      setParameters({
                        ...parameters,
                        initial_credits: numValue,
                      });
                    }
                  }}
                />
                <span className="text-xs text-gray-400">
                  Este valor se utiliza al crear el perfil de un nuevo usuario mediante la función
                  <span className="font-mono"> get_initial_credits()</span>.
                </span>
              </label>
            </div>
          </div>
        </div>

        <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-5 shadow">
          <h2 className="text-lg font-semibold mb-4">Información</h2>
          <div className="space-y-3 text-sm text-gray-300">
            <p>
              El sistema utiliza la siguiente fórmula para calcular precios:
            </p>
            <div className="bg-gray-900/50 rounded-md p-3 font-mono text-xs text-gray-200">
              Precio = PrecioMin + (Creditos - CreditosMin) × (PrecioMax - PrecioMin) / (CreditosMax - CreditosMin)
            </div>
            <p className="text-xs text-gray-400 mt-3">
              Los valores de CreditosMin y CreditosMax se definen en otro lugar del sistema.
            </p>
          </div>
        </div>
      </div>

      <div className="sticky bottom-4 mt-8 flex justify-end">
        <button
          className={`px-5 py-2 rounded-md text-white shadow-lg transition ${isValid ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-600 cursor-not-allowed'}`}
          onClick={handleSave}
          disabled={!isValid || saving}
        >
          {saving ? 'Guardando…' : 'Guardar Cambios'}
        </button>
      </div>

      <SystemParametersAuditModal
        isOpen={isAuditModalOpen}
        onClose={() => setIsAuditModalOpen(false)}
        refreshTrigger={auditRefreshTrigger}
      />
    </div>
  );
};

export default SystemParameters; 